import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { store } from '../..';
import { queryClient } from '../../..';
import { employeeSalaryAdvance } from '../../../api/employees/salary-advance';
import { setAlert, setIsLoading } from '../../components/components-slice';
import { formatNumber } from '../../../utils/functions';

export function useGetEmployeeSalaryAdvance(limit, page, statusFilter, search) {
  return useQuery(
    ['employee-salary-advances', { limit, page, statusFilter, search }],
    () => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.getEmployeeSalaryAdvance({
        limit,
        page,
        statusFilter,
        search
      });
    },
    {
      select: (data) => {
        data?.data?.forEach((employee) => {
          employee.created = moment(employee.created).format('MMMM D, YYYY');
          employee.formatted_amount = formatNumber(parseFloat(employee.amount).toFixed(2));
          employee.formatted_month = moment(employee.month).format('MMMM, YYYY');
          employee.employeeName = `${employee.first_name} ${employee.last_name}`;
          employee.status = parseInt(employee.status)
          switch (parseInt(employee.status)) {
            case 2:
              employee.computedActions = [0, 1];
              break;
            default:
              break;
          }
        });
        return data;
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useGetFullSalaryAdvanceReport(start_date, end_date) {
  return useQuery(
    ['salary-advance-full-report', { start_date, end_date }],
    () => {
      return employeeSalaryAdvance.getSalaryAdvanceFullReport({ start_date, end_date });
    },
    {
      select: (data) => {
        let formatted_months = []
        data?.months?.forEach(element => {
          formatted_months.push(moment(element+'-01').format('MMM YY'));
        });
        data.formatted_months = formatted_months;
        return data;
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
    
  );
}

export function useGetSalaryAdvanceReport() {
  return useQuery(
    ['employee-salary-advance-report'],
    () => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.getSalaryAdvanceReport({
      });
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useAddSalaryAdvance() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.addSalaryAdvance(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-salary-advances');
        store.dispatch(setAlert(true, 'success', 'Added successfully'));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useEditSalaryAdvance() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.editSalaryAdvance(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('employee-salary-advances');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useBulkAddSalaryAdvance() {
  return useMutation(
    (payload) => {
      return employeeSalaryAdvance.bulkAddSalaryAdvance(payload);
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('employee-salary-advances');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (response, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useDeleteSalaryAdvance() {
  return useMutation(
    (payload) => {
      return employeeSalaryAdvance.deleteSalaryAdvance(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employee-salary-advances');
        store.dispatch(setAlert(true, 'success', 'Salary advance deleted successfully'));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useSingleGetEmployeeSalaryAdvances(id, limit, page) {
  return useQuery(
    ['single-employee-salary-advance', {id, limit, page}],
    () => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.getSingleEmployeeSalaryAdvances({id, limit, page});
    },
    {
      enabled: id !== null,
      select: (data) => {
        data?.data?.forEach((employee) => {
          employee.created = moment(employee.created).format('MMMM D, YYYY');
          employee.formatted_month = moment(employee.month).format('MMMM, YYYY');
          employee.amount = parseFloat(employee.amount);
          employee.formatted_amount = formatNumber(parseFloat(employee.amount).toFixed(2));
          employee.employeeName = `${employee.first_name} ${employee.last_name}`;
          employee.status = parseInt(employee.status)
          if (employee.status == 0) {
            employee.status = 2
          }
          switch (parseInt(employee.status)) {
            case 2:
              employee.computedActions = [0, 1];
              break;
            default:
              break;
          }
        });
        return data;
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useGetSalaryAdvanceApplications(
  limit,
  page,
  search,
  statusFilter,
  start_date,
  end_date,
  sort_by,
  sort_order,
) {
  return useQuery(
    [
      'salary-advance-applications',
      { limit, page, search, statusFilter, start_date, end_date, sort_by, sort_order },
    ],
    () => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.getSalaryAdvanceApplications({
        limit,
        page,
        search,
        statusFilter,
        start_date,
        end_date,
        sort_by,
        sort_order,
      });
    },
    {
      select: (data) => {
        data?.data?.forEach((application) => {
          application.created = application.created.split('T')[0];
          application.amount = parseFloat(
            application.amount,
          );
          application.employeeName = `${application.first_name} ${application.last_name}`;
          application.formatted_repayment_start = moment(application.repayment_start).format('MMMM, YYYY')
          application.formatted_repayment_end = moment(application.repayment_end).format('MMMM, YYYY')
          //compute action items
          switch (application.status) {
            case 0:
              application.computedActions = [0, 2];
              break;
            case 3:
              application.computedActions = [1, 2, 3];
              break;
            default:
              break;
          }
        });

        return data;
      },

      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useAddSalaryAdvanceApplication() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.addSalaryAdvanceApplication(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('salary-advance-applications');
        store.dispatch(setAlert(true, 'success', 'Added successfully'));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}
//New
export function useSingleGetEmployeeSalaryAdvanceApplications(id, limit, page, search) {
  return useQuery(
    ['single-salary-advance-application', { id, limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.getSingleEmployeeSalaryAdvanceApplications({ id, limit, page, search });
    },
    {
      select: (data) => {
        data?.data?.forEach((application) => {
          application.created = application.created.split('T')[0];
          application.amount = parseFloat(
            application.amount,
          );
          application.employeeName = `${application.first_name} ${application.last_name}`;
          //compute action items
          switch (application.status) {
            case 0:
              application.computedActions = [0, 2];
              break;
            case 3:
              application.computedActions = [1, 2, 3];
              break;
            default:
              break;
          }
        });

        return data;
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useDisburseSalaryAdvanceApplication() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.disburseApplication(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('salary-advance-applications');
        queryClient.invalidateQueries('single-salary-advance-application');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useApprovePaySalaryAdvanceApplication() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.approvePaySalaryAdvance(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('salary-advance-applications');
        queryClient.invalidateQueries('single-salary-advance-application');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useApproveSalaryAdvance() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.approveSalaryAdvance(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('salary-advance-applications');
        queryClient.invalidateQueries('single-salary-advance-application');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useDeclineSalaryAdvanceApplication() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.declineSalaryAdvanceApplication(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('salary-advance-applications');
        queryClient.invalidateQueries('single-salary-advance-application');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useDeclineSalaryAdvanceApplicationDisbursement() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return employeeSalaryAdvance.declineApplicationDisbursement(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('salary-advance-applications');
        queryClient.invalidateQueries('single-salary-advance-application');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

